import React from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Per-session scroll memory, keyed by React Router's history-entry key
 * (`location.key`). A back/forward POP returns to an existing entry with its
 * original key, so its saved offset is found and restored; a fresh PUSH gets a
 * brand-new key with nothing stored, so that page starts at the top.
 */
const positions = new Map<string, number>();

/**
 * Remembers and restores the scroll offset of the nova scroll
 * container — the `PageShell` element (`.ai-bb-page-root`) that owns
 * `overflow-y: auto` — found inside `layerRef`.
 *
 * Why here and not on the window: the config-driven layout pins the document to
 * the viewport height, so each page scrolls inside its own container and native
 * browser scroll restoration never applies.
 *
 * Why pin the key on mount instead of reading it live: `PageTransition` keeps the
 * outgoing page mounted as a frozen instance while it slides away, and that
 * instance's `useLocation()` now reports the *incoming* page's key. Reacting to
 * the live key would reset the leaving page to the top mid-animation (and write
 * its scroll onto the wrong entry). Every page-layer instance is born as the
 * entering page, so capturing the key once on mount ties save + restore to that
 * page's own history entry for its whole life.
 */
export const useScrollRestoration = (layerRef: React.RefObject<HTMLElement>): void => {
  const location = useLocation();
  const keyRef = React.useRef(location.key);

  // Layout effect so the restore lands before paint — the page never flashes at
  // the top before jumping down. Runs once per layer instance (the ref is stable).
  React.useLayoutEffect(() => {
    const el = layerRef.current?.querySelector<HTMLElement>('.ai-bb-page-root');
    if (!el) return undefined;
    const key = keyRef.current;

    el.scrollTop = positions.get(key) ?? 0;

    const onScroll = (): void => {
      positions.set(key, el.scrollTop);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [layerRef]);
};

export default useScrollRestoration;
